import { JsonParsing, ParseError } from "../../dto/parsing/JsonParsing";
import { ok, Result } from "neverthrow";
import { ACCESS_TOKEN_TO_USE_STORAGE_KEY } from "../../auth/stub/StubTokenDefinition";
import { LocalStorageKey } from "./LocalStorageKey";

export const I18NEXT_STORAGE_KEY = "i18nextLng";

export default class LocalStoragePersistence {
  private static readonly LOCAL_STORAGE_KEY_IDENTIFIER_SEPARATOR = "-";

  private static readonly ENTRIES_TO_NOT_BE_REMOVED_DURING_DELETION = [
    ACCESS_TOKEN_TO_USE_STORAGE_KEY,
    I18NEXT_STORAGE_KEY,
  ];

  public static extractIdentifierFromLocalStorageKey(
    entityName: string,
    localStorageKey: string,
  ): string | undefined {
    return localStorageKey.replace(
      entityName + this.LOCAL_STORAGE_KEY_IDENTIFIER_SEPARATOR,
      "",
    );
  }

  private static isKeyValidEntityIdentifier(
    entityName: string,
    localStorageKey: string,
  ): boolean {
    return localStorageKey.startsWith(
      entityName + this.LOCAL_STORAGE_KEY_IDENTIFIER_SEPARATOR,
    );
  }

  public static read<T>(
    key: LocalStorageKey,
  ): Result<T | undefined, ParseError> {
    const localStorageValue = localStorage.getItem(key.localStorageKey);
    if (
      localStorageValue &&
      localStorageValue !== "undefined" //Because localStorage.setItem("aKey", undefined) will read as "undefined"
    ) {
      return new JsonParsing().parse(localStorageValue);
    } else {
      return ok(undefined);
    }
  }

  public static persist<T>(key: LocalStorageKey, newValue: T) {
    console.debug(
      "persisting state to local storage " + key.localStorageKey,
      newValue,
    );
    localStorage.setItem(key.localStorageKey, JSON.stringify(newValue));
  }

  public static doesKeyExist(key: LocalStorageKey): boolean {
    const localStorageValue = localStorage.getItem(key.localStorageKey);
    return !!localStorageValue;
  }

  public static loadAllIdentifiersForEntity(
    entityName: string,
  ): ReadonlyArray<string> {
    return Object.keys(localStorage)
      .filter((key) => this.isKeyValidEntityIdentifier(entityName, key))
      .map((localStorageKey) =>
        LocalStoragePersistence.extractIdentifierFromLocalStorageKey(
          entityName,
          localStorageKey,
        ),
      )
      .filter((identifier): identifier is string => identifier !== undefined);
  }

  public clearLocalStorageWithExceptions() {
    const backedUpEntries: Map<string, string | null> = new Map();
    LocalStoragePersistence.ENTRIES_TO_NOT_BE_REMOVED_DURING_DELETION.forEach(
      (key) => {
        backedUpEntries.set(key, localStorage.getItem(key));
      },
    );

    localStorage.clear();

    backedUpEntries.forEach((value, key) => {
      if (value !== null) {
        localStorage.setItem(key, value);
      }
    });
  }
}
